"use client";

import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { useRedirectAfterSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useRedirectAfterSignUp";
import { useSignUpEventTracker } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSignUpStepsStore } from "@faire/retailer-visitor-shared/views/SignUp/SignUpStepsContext";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { foodAndBeverageImages } from "@faire/retailer-visitor-shared/views/SignUp/success/__internal__/loadingPageImages";
import {
  fadeIn,
  fadeOut,
  translateDownImage1,
  translateDownImage2,
  translateLeftImage1,
  translateLeftImage2,
  translateRightImage1,
  translateRightImage2,
  translateUpImage1,
  translateUpImage2,
} from "@faire/retailer-visitor-shared/views/SignUp/success/__internal__/styled";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

type LongDirection = "up" | "down";
type ShortDirection = "left" | "right";
const DELAY = 1;
const WIDTH = 234;

export const SignUpLoadingPage: React.FC = () => {
  const [startExitAnimation, setStartExitAnimation] = React.useState(false);
  const { trackView, trackSubmit } = useSignUpEventTracker(Step.END);
  const [, setSignUpStepsStore] = useSignUpStepsStore([]);
  const [{ user }] = useSignUpRequest(["user"]);
  const { firstName } = useUserStore();
  const redirectAfterSignUp = useRedirectAfterSignUp();

  useEffect(() => {
    trackView();
    setSignUpStepsStore({
      currentStep: Step.END,
    });
  }, [setSignUpStepsStore, trackView]);

  const isSSR = useIsSSR();
  const defaultImage: Partial<IImage> = {
    url: EAEAEA_PIXEL,
  };
  const name = user?.first_name ?? firstName;

  useEffect(() => {
    let cleanupTimeout: number | undefined = undefined;
    if (startExitAnimation) {
      cleanupTimeout = setTimeoutIfPossible(() => {
        trackSubmit();
        redirectAfterSignUp();
        signUpCache.clear();
      }, 300);
    }

    return () => {
      if (cleanupTimeout) {
        clearTimeout(cleanupTimeout);
      }
    };
  }, [redirectAfterSignUp, startExitAnimation, trackSubmit]);
  return (
    <PagePadding>
      <Container>
        <Spacer height={{ mobileAndAbove: "2x", tabletAndAbove: "8x" }} />
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
          }}
        >
          <StrictLocalMsg
            defaultMessage="Curating products for {userPersonName}’s Store..."
            description={{
              text: "Title for animation leting retailers know Faire is finding products",
              img: "https://cdn.faire.com/fastly/3fe7f04732af81f786f641edf0c70898b322204a60fb836820747f24de9dccf1.png",
            }}
            values={{ userPersonName: name }}
          />
        </Typography>
        <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }} />
        <LoadingPageGrid>
          <TopLeftBox>
            <LongImage1
              $direction="up"
              src={optimizeImageUrl(
                foodAndBeverageImages.topLeft[0] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
            <LongImage2
              $direction="up"
              src={optimizeImageUrl(
                foodAndBeverageImages.topLeft[1] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
          </TopLeftBox>
          <TopRightBox>
            <ShortImage1
              $direction="right"
              src={optimizeImageUrl(
                foodAndBeverageImages.topRight[0] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
            <ShortImage2
              $direction="right"
              onAnimationEnd={() =>
                setTimeout(() => setStartExitAnimation(true), 1500)
              }
              src={optimizeImageUrl(
                foodAndBeverageImages.topRight[1] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
          </TopRightBox>
          <BottomLeftBox>
            <ShortImage1
              $direction="left"
              src={optimizeImageUrl(
                foodAndBeverageImages.bottomLeft[0] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
            <ShortImage2
              $direction="left"
              src={optimizeImageUrl(
                foodAndBeverageImages.bottomLeft[1] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
          </BottomLeftBox>
          <BottomRightBox>
            <LongImage1
              $direction="down"
              src={optimizeImageUrl(
                foodAndBeverageImages.bottomRight[0] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
            <LongImage2
              $direction="down"
              src={optimizeImageUrl(
                foodAndBeverageImages.bottomRight[1] ?? defaultImage,
                isSSR,
                {
                  width: WIDTH,
                  resizeMethod: "fill",
                }
              )}
            />
          </BottomRightBox>
        </LoadingPageGrid>
      </Container>
    </PagePadding>
  );
};

const PagePadding = styled.div`
  padding: ${getSpacing("3x")};
`;

const Container = styled(Column)<{
  $startExitAnimation?: boolean;
}>`
  max-width: 480px;
  margin: auto;
  ${({ $startExitAnimation }) =>
    $startExitAnimation &&
    css`
      animation: fadeOut 0.3s ease-out forwards;
    `}
`;

const LoadingPageGrid = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-gap: 12px;
  border-radius: ${getSpacing("1x")};
`;

const TopLeftBox = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 2/3;
  grid-area: 1 / 1 / 3 / 2;
`;

const TopRightBox = styled.div`
  height: 100%;
  position: relative;
  overflow: hidden;
  grid-area: 1 / 2 / 2 / 3;
`;

const BottomLeftBox = styled.div`
  height: 100%;
  position: relative;
  overflow: hidden;
  grid-area: 3 / 1 / 4 / 2;
`;

const BottomRightBox = styled.div`
  aspect-ratio: 2/3;
  position: relative;
  overflow: hidden;
  grid-area: 2 / 2 / 4 / 3;
`;

const LongImage1 = styled.img<{
  $direction: LongDirection;
}>`
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
  animation:
    ${(props) =>
        props.$direction === "up" ? translateUpImage1 : translateDownImage1}
      1s ease-out ${DELAY}s 1 forwards,
    ${fadeOut} 1s ease-out ${DELAY}s 1 forwards;
`;

const LongImage2 = styled.img<{
  $direction: LongDirection;
}>`
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
  animation:
    ${(props) =>
        props.$direction === "up" ? translateUpImage2 : translateDownImage2}
      1s ease-out ${DELAY}s 1 forwards,
    ${fadeIn} 1s ease-out ${DELAY}s 1 forwards;
`;

const ShortImage1 = styled.img<{
  $direction: ShortDirection;
}>`
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
  animation:
    ${(props) =>
        props.$direction === "left"
          ? translateRightImage1
          : translateLeftImage1}
      1s ease-out ${DELAY + 2}s 1 forwards,
    ${fadeOut} 1s ease-out ${DELAY + 2}s 1 forwards;
`;

const ShortImage2 = styled.img<{
  $direction: ShortDirection;
}>`
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
  animation:
    ${(props) =>
        props.$direction === "left"
          ? translateRightImage2
          : translateLeftImage2}
      1s ease-out ${DELAY + 2}s 1 forwards,
    ${fadeIn} 1s ease-out ${DELAY + 2}s 1 forwards;
`;
